import { useRef, useState } from "react";
import "./InputDocument.scss";
import { useTranslation } from "react-i18next";

interface InputDocumentProps {
    onFileChange: (file: File | null) => void;
    required?: boolean;
    className?: string;
    label?: string;
    existingDocumentName?: string;
}

const InputDocument: React.FC<InputDocumentProps> = ({
    onFileChange,
    required = false,
    className = "",
    label,
    existingDocumentName,
}) => {
    const { t } = useTranslation();
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;

        if (files.length > 0) {
            handleFileChange(files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileChange(e.target.files[0]);
        }
    };

    const handleFileChange = (file: File) => {
        setUploadedFile(file);
        onFileChange(file);
    };

    const openFileExplorer = () => {
        fileInputRef.current?.click();
    };

    const hasExistingDocument = Boolean(existingDocumentName) && !uploadedFile;

    return (
        <div className="file-uploader">
            {label && (
                <label className="input__label">
                    {label}
                    {required && (
                        <span className="input-dropdown__required">*</span>
                    )}
                </label>
            )}

            <div
                className={`${className} drop-zone ${
                    isDragging ? "dragging" : ""
                } ${uploadedFile || hasExistingDocument ? "has-file" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileExplorer}
                role="button"
                tabIndex={0}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                    aria-label={(t("ui.inputs.selectFile") ?? "")}
                    required={required && !existingDocumentName}
                />

                <div className="upload-status">
                    {uploadedFile ? (
                        <>
                            <span className="file-name">
                                {uploadedFile.name}
                            </span>
                            <span className="file-size">
                                ({(uploadedFile.size / 1024).toFixed(2)} KB)
                            </span>
                        </>
                    ) : hasExistingDocument ? (
                        <>
                            <span className="file-name">
                                {t("ui.inputs.currentDocument", {
                                    name: existingDocumentName,
                                })}
                            </span>
                            <span className="file-hint">
                                {t("ui.inputs.clickToReplace")}
                            </span>
                        </>
                    ) : (
                        <span>{t("ui.inputs.dropFile")}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InputDocument;
